import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Download, Upload, Check, AlertTriangle, HelpCircle, Search } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { localISODate } from '../lib/utils';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_META = {
  exact: { icon: Check, label: 'Dopasowano', className: 'text-green-500 bg-green-500/10 border-green-500/30' },
  fuzzy: { icon: AlertTriangle, label: 'Sprawdź dopasowanie', className: 'text-yellow-500 bg-yellow-500/10 border-yellow-500/30' },
  none: { icon: HelpCircle, label: 'Wybierz ręcznie', className: 'text-red-400 bg-red-500/10 border-red-500/30' },
};

export const ExcelImportPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [rows, setRows] = useState(null);
  const [availableSheets, setAvailableSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [planInfo, setPlanInfo] = useState({
    plan_name: '',
    frequency: 3,
    start_date: localISODate(),
  });
  const [saving, setSaving] = useState(false);
  const [pickerRow, setPickerRow] = useState(null);
  const [pickerSearch, setPickerSearch] = useState('');

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [clientRes, exercisesRes] = await Promise.all([
        axios.get(`${API_URL}/api/clients/${id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/exercises`, { withCredentials: true }),
      ]);
      setClient(clientRes.data);
      setExercises(exercisesRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    window.open(`${API_URL}/api/training-plans/import-template`, '_blank');
  };

  const handleUpload = async (sheetOverride) => {
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const sheetToUse = sheetOverride !== undefined ? sheetOverride : selectedSheet;
      if (sheetToUse) formData.append('sheet_name', sheetToUse);
      const res = await axios.post(`${API_URL}/api/training-plans/import-excel-preview`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const augmented = res.data.rows.map(r => ({
        ...r,
        resolved_exercise_id: r.matched_exercise_id,
        resolved_exercise_name: r.matched_exercise_name,
      }));
      setRows(augmented);
      setWarnings(res.data.warnings || []);
      setAvailableSheets(res.data.available_sheets || []);
      setSelectedSheet(res.data.used_sheet || '');
      setPlanInfo(prev => ({
        ...prev,
        frequency: res.data.frequency || prev.frequency,
        plan_name: prev.plan_name || file.name.replace(/\.xlsx?$/i, ''),
      }));
    } catch (error) {
      setUploadError(error.response?.data?.detail || 'Błąd wczytywania pliku');
    } finally {
      setUploading(false);
    }
  };

  const handleSheetChange = (sheet) => {
    setSelectedSheet(sheet);
    handleUpload(sheet);
  };

  const handlePickExercise = (exercise) => {
    setRows(prev => prev.map((r, i) =>
      i === pickerRow
        ? { ...r, resolved_exercise_id: exercise.id, resolved_exercise_name: exercise.name }
        : r
    ));
    setPickerRow(null);
    setPickerSearch('');
  };

  const allResolved = rows && rows.length > 0 && rows.every(r => r.resolved_exercise_id);

  const handleCreatePlan = async () => {
    if (!planInfo.plan_name || !allResolved) return;
    setSaving(true);
    try {
      const dayCounters = {};
      const finalExercises = rows.map(r => {
        const orderIndex = dayCounters[r.day_number] || 0;
        dayCounters[r.day_number] = orderIndex + 1;
        return {
          exercise_id: r.resolved_exercise_id,
          exercise_name: r.resolved_exercise_name,
          day_number: r.day_number,
          order_index: orderIndex,
          sets: r.sets,
          reps: r.reps,
          tempo: r.tempo,
          rir: r.rir,
          rpe: r.rpe,
          rest_seconds: r.rest_seconds,
          weight_kg: r.weight_kg,
          notes: r.notes,
          superset_group: r.superset_group,
          circuit_group: null,
          day_label: r.day_label,
        };
      });
      await axios.post(`${API_URL}/api/training-plans`, {
        client_id: id,
        plan_name: planInfo.plan_name,
        frequency: planInfo.frequency,
        start_date: planInfo.start_date || null,
        end_date: null,
        notes: '',
        exercises: finalExercises,
      }, { withCredentials: true });
      navigate(`/client/${id}`);
    } catch (error) {
      console.error('Error creating plan:', error);
      toast.error('Błąd zapisu planu');
    } finally {
      setSaving(false);
    }
  };

  const filteredPickerExercises = exercises.filter(ex =>
    ex.name.toLowerCase().includes(pickerSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-white">Ładowanie...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="excel-import-page">
      <nav className="border-b border-[#2A2A2A] px-4 sm:px-8 py-4 sm:py-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
          <Logo size="md" />
          <Button
            onClick={() => navigate(`/client/${id}`)}
            variant="ghost"
            className="text-[#A1A1A1] hover:text-white px-2 sm:px-4 shrink-0"
          >
            <ArrowLeft className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Powrót</span>
          </Button>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <div className="mb-12">
          <h1 className="display-text text-5xl text-white mb-2">Import Planu z Excela</h1>
          <p className="body-text text-[#A1A1A1] text-lg">{client?.name}</p>
        </div>

        {/* Step 1: template */}
        <div className="card-surface rounded-3xl p-8 mb-8">
          <h2 className="heading-text text-xl text-white mb-2">1. Pobierz szablon</h2>
          <p className="text-sm text-[#A1A1A1] mb-4">
            Wypełnij szablon planem treningowym, nie zmieniając nagłówków kolumn.
          </p>
          <Button
            onClick={handleDownloadTemplate}
            variant="ghost"
            className="text-[#C9A84C] hover:text-white border border-[#C9A84C]/50 rounded-full"
            data-testid="download-template-button"
          >
            <Download className="w-4 h-4 mr-2" />
            Pobierz szablon (.xlsx)
          </Button>
        </div>

        {/* Step 2: upload */}
        <div className="card-surface rounded-3xl p-8 mb-8">
          <h2 className="heading-text text-xl text-white mb-2">2. Wgraj wypełniony plik</h2>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-4">
            <input
              type="file"
              accept=".xlsx,.xlsm"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm text-[#A1A1A1] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border file:border-[#2A2A2A] file:bg-[#0A0A0A] file:text-white"
              data-testid="excel-file-input"
            />
            <Button
              onClick={() => handleUpload()}
              disabled={!file || uploading}
              className="bg-[#1E1E1E] hover:bg-[#2A2A2A] text-white border border-[#C9A84C] rounded-full shrink-0"
              data-testid="upload-preview-button"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Wczytywanie...' : 'Wgraj i podejrzyj'}
            </Button>
          </div>
          {uploadError && <p className="text-sm text-red-400 mt-3">{uploadError}</p>}

          {availableSheets.length > 1 && (
            <div className="mt-4">
              <Label className="label-text text-[#A1A1A1] mb-2 block">
                ARKUSZ (PLIK MA {availableSheets.length} ARKUSZY - np. tygodnie mezocyklu, zaimportuj każdy osobno jako plan)
              </Label>
              <Select value={selectedSheet} onValueChange={handleSheetChange}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white max-w-xs" data-testid="sheet-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1E1E1E] border-[#2A2A2A] text-white">
                  {availableSheets.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Step 3: review */}
        {rows && (
          <>
            <div className="card-surface rounded-3xl p-8 mb-8">
              <h2 className="heading-text text-xl text-white mb-6">3. Informacje o planie</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label className="label-text text-[#A1A1A1] mb-2 block">NAZWA PLANU</Label>
                  <Input
                    value={planInfo.plan_name}
                    onChange={(e) => setPlanInfo({ ...planInfo, plan_name: e.target.value })}
                    className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                    data-testid="import-plan-name"
                  />
                </div>
                <div>
                  <Label className="label-text text-[#A1A1A1] mb-2 block">CZĘSTOTLIWOŚĆ (DNI/TYG)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="7"
                    value={planInfo.frequency}
                    onChange={(e) => setPlanInfo({ ...planInfo, frequency: parseInt(e.target.value) })}
                    className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                  />
                </div>
                <div>
                  <Label className="label-text text-[#A1A1A1] mb-2 block">DATA ROZPOCZĘCIA</Label>
                  <Input
                    type="date"
                    value={planInfo.start_date}
                    onChange={(e) => setPlanInfo({ ...planInfo, start_date: e.target.value })}
                    className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                  />
                </div>
              </div>
            </div>

            {warnings.length > 0 && (
              <div className="mb-8 p-4 rounded-2xl bg-yellow-500/10 border border-yellow-500/30">
                <p className="text-sm text-yellow-500 label-text mb-2">POMINIĘTE WIERSZE</p>
                {warnings.map((w, i) => <p key={i} className="text-xs text-[#A1A1A1]">{w}</p>)}
              </div>
            )}

            <div className="card-surface rounded-3xl p-8 mb-8">
              <h2 className="heading-text text-xl text-white mb-6">4. Sprawdź dopasowane ćwiczenia</h2>
              <div className="space-y-3">
                {rows.map((row, i) => {
                  const meta = STATUS_META[row.resolved_exercise_id === row.matched_exercise_id ? row.match_status : 'exact'];
                  const StatusIcon = meta.icon;
                  return (
                    <div
                      key={i}
                      className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-2xl bg-[#0A0A0A] border border-[#2A2A2A]"
                      data-testid={`import-row-${i}`}
                    >
                      <span className="label-text text-[#A1A1A1] text-xs w-24 shrink-0" title={row.day_label ? `Dzień ${row.day_number}` : undefined}>
                        {row.day_label || `DZIEŃ ${row.day_number}`}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-[#A1A1A1]">{row.exercise_name_raw}</p>
                        <p className="text-white font-medium truncate">
                          {row.resolved_exercise_name || 'Brak dopasowania'}
                        </p>
                      </div>
                      <span className={`flex items-center gap-1 text-xs px-3 py-1 rounded-full border shrink-0 ${meta.className}`}>
                        <StatusIcon className="w-3 h-3" />
                        {meta.label}
                      </span>
                      <Button
                        onClick={() => { setPickerRow(i); setPickerSearch(''); }}
                        variant="ghost"
                        size="sm"
                        className="text-[#C9A84C] hover:text-white shrink-0"
                        data-testid={`fix-row-${i}`}
                      >
                        <Search className="w-4 h-4 mr-1" />
                        Zmień
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end gap-4">
              <Button onClick={() => navigate(`/client/${id}`)} variant="ghost" className="text-[#A1A1A1]">
                Anuluj
              </Button>
              <Button
                onClick={handleCreatePlan}
                disabled={saving || !planInfo.plan_name || !allResolved}
                className="bg-[#1E1E1E] hover:bg-[#2A2A2A] text-white border border-[#C9A84C] rounded-full px-12 py-6 text-lg"
                data-testid="create-plan-from-import"
              >
                {saving ? 'Zapisywanie...' : allResolved ? 'Utwórz plan' : 'Dopasuj wszystkie ćwiczenia'}
              </Button>
            </div>
          </>
        )}
      </main>

      <Dialog open={pickerRow !== null} onOpenChange={(open) => !open && setPickerRow(null)}>
        <DialogContent className="bg-[#1E1E1E] border-[#2A2A2A] rounded-3xl max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="heading-text text-xl text-white">Wybierz ćwiczenie</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <div className="relative mb-4">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A1A1A1]" />
              <Input
                placeholder="Szukaj ćwiczenia..."
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                className="bg-[#0A0A0A] border-[#2A2A2A] rounded-full text-white pl-12"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              {filteredPickerExercises.map(ex => (
                <button
                  key={ex.id}
                  onClick={() => handlePickExercise(ex)}
                  className="w-full text-left p-4 rounded-2xl bg-[#0A0A0A] hover:bg-[#2A2A2A] border border-[#2A2A2A] hover:border-[#C9A84C]/50 transition-all"
                  data-testid={`picker-option-${ex.id}`}
                >
                  <div className="text-white font-medium">{ex.name}</div>
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
